import { type HTMLAttributes, type TdHTMLAttributes, type ThHTMLAttributes, forwardRef } from 'react';
import clsx from 'clsx';

// Table Container
export interface TableContainerProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const TableContainer = forwardRef<HTMLDivElement, TableContainerProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={clsx('overflow-x-auto rounded-xl border border-slate-200', className)} {...props}>
      {children}
    </div>
  )
);
TableContainer.displayName = 'TableContainer';

// Table
export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  children: React.ReactNode;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, children, ...props }, ref) => (
    <table ref={ref} className={clsx('w-full text-sm', className)} {...props}>
      {children}
    </table>
  )
);
Table.displayName = 'Table';

// Table Head
export const TableHead = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, children, ...props }, ref) => (
    <thead ref={ref} className={clsx('bg-slate-50', className)} {...props}>
      {children}
    </thead>
  )
);
TableHead.displayName = 'TableHead';

// Table Body
export const TableBody = forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, children, ...props }, ref) => (
    <tbody ref={ref} className={clsx('divide-y divide-slate-100', className)} {...props}>
      {children}
    </tbody>
  )
);
TableBody.displayName = 'TableBody';

// Table Row
export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  isSelected?: boolean;
  isHoverable?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, isSelected, isHoverable = true, children, ...props }, ref) => (
    <tr
      ref={ref}
      className={clsx(
        'transition-colors',
        isHoverable && 'hover:bg-slate-50',
        isSelected && 'bg-primary-50',
        className
      )}
      {...props}
    >
      {children}
    </tr>
  )
);
TableRow.displayName = 'TableRow';

// Table Header Cell
export const TableHeaderCell = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, children, ...props }, ref) => (
    <th
      ref={ref}
      className={clsx(
        'px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider',
        className
      )}
      {...props}
    >
      {children}
    </th>
  )
);
TableHeaderCell.displayName = 'TableHeaderCell';

// Table Cell
export const TableCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, children, ...props }, ref) => (
    <td ref={ref} className={clsx('px-4 py-3 text-slate-700', className)} {...props}>
      {children}
    </td>
  )
);
TableCell.displayName = 'TableCell';

// Empty State
export interface TableEmptyProps {
  message?: string;
  description?: string;
  action?: React.ReactNode;
  colSpan?: number;
}

export function TableEmpty({
  message = 'No data found',
  description,
  action,
  colSpan = 6,
}: TableEmptyProps) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-12 text-center">
        <div className="text-slate-400">
          <svg
            className="w-12 h-12 mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
          <p className="text-slate-600 font-medium">{message}</p>
          {description && <p className="text-sm text-slate-400 mt-1">{description}</p>}
          {action && <div className="mt-4">{action}</div>}
        </div>
      </td>
    </tr>
  );
}

